import { useState } from 'react';
import { getParticipantColor } from './SessionCard';

interface SessionParticipant {
  name: string;
  points: number;
  colorIndex: number;
}

interface Session {
  sessionId: number;
  importance: number;
  invalidated: boolean;
  timestamp?: number;
  participants: SessionParticipant[];
}

interface SessionTableProps {
  sessions: Session[];
  isSigner: boolean;
  isFinalized: boolean;
  invalidatingSession: number | null;
  onInvalidate: (sessionId: number) => void;
}

export function SessionTable({
  sessions,
  isSigner,
  isFinalized,
  invalidatingSession,
  onInvalidate,
}: SessionTableProps) {
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const formatTimestamp = (ts: number | undefined, includeTime = false) => {
    if (!ts) return '—';
    const date = new Date(ts * 1000);
    const dateStr = date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    if (!includeTime) return dateStr;
    const timeStr = date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${dateStr} at ${timeStr}`;
  };

  const renderImportanceDots = (imp: number) => {
    return (
      <span className="importance-dots">
        {Array.from({ length: 5 }, (_, i) => (
          <span
            key={i}
            className={`importance-dot ${i < imp ? 'active' : ''}`}
          />
        ))}
      </span>
    );
  };

  const toggleRow = (sessionId: number) => {
    setExpandedRow(expandedRow === sessionId ? null : sessionId);
  };

  return (
    <div className="session-table-wrapper">
      <table className="session-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Participants</th>
            <th>Date</th>
            <th>Importance</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((session) => {
            const activeParticipants = session.participants.filter(p => p.points > 0);
            const isExpanded = expandedRow === session.sessionId;

            return (
              <>
                <tr
                  key={session.sessionId}
                  className={`session-table-row ${session.invalidated ? 'invalidated' : ''} ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleRow(session.sessionId)}
                >
                  <td className="session-table-number">{session.sessionId + 1}</td>
                  <td className="session-table-participants">
                    <span className="participant-icon">👤</span> {activeParticipants.length}
                  </td>
                  <td className="session-table-date">{formatTimestamp(session.timestamp)}</td>
                  <td className="session-table-importance">{renderImportanceDots(session.importance)}</td>
                  <td className="session-table-status">
                    {session.invalidated ? (
                      <span className="session-status invalidated">Invalidated</span>
                    ) : (
                      <span className="session-status verified">Verified</span>
                    )}
                  </td>
                </tr>
                {isExpanded && (
                  <tr key={`${session.sessionId}-expanded`} className="session-table-expanded-row">
                    <td colSpan={5}>
                      <div className="session-table-expanded-content">
                        {session.timestamp && (
                          <div className="session-table-time">
                            {formatTimestamp(session.timestamp, true)}
                          </div>
                        )}
                        <div className="session-table-participants-list">
                          {activeParticipants.map((p, i) => (
                            <div key={i} className="participant-row">
                              <span
                                className="participant-color-indicator"
                                style={{ backgroundColor: getParticipantColor(p.colorIndex) }}
                              />
                              <span className="participant-name">{p.name}</span>
                              <span className="participant-points">{p.points} pts</span>
                            </div>
                          ))}
                        </div>

                        {isSigner && !isFinalized && !session.invalidated && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm(`Invalidate session ${session.sessionId + 1}? This cannot be undone.`)) {
                                onInvalidate(session.sessionId);
                              }
                            }}
                            disabled={invalidatingSession === session.sessionId}
                            className="btn-secondary btn-small"
                          >
                            {invalidatingSession === session.sessionId ? 'Invalidating...' : 'Invalidate'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
